import React, { useState, useEffect } from 'react';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import { QuestionProps, ModuleProps } from 'global/types';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";

import './index.css';

type DragAndDropBlankProps = {
    question: QuestionProps;
    modules: ModuleProps[];
    currentModule: ModuleProps;
    autoSave: (modules: ModuleProps[]) => void;
    setModules: (modules: ModuleProps[]) => void;
};

function DragAndDropBlank({ question, modules, currentModule, autoSave, setModules } : DragAndDropBlankProps) {
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [questionHelper, setQuestionHelper] = useState(null);

    const currentModuleIndex = modules.findIndex(module => module.id === currentModule.id);
    const currentAnswer = modules[currentModuleIndex].answers.find(answer => answer.questionNum === question.questionNum)?.answer;

    const generateQuestionHelper = (helper: string) => {
        let html = helper;

        const notBoxRegex = /(?!\[\[)(.*?)(?=\]\])/g;
        const boxRegex = /\[\[(.*?)\]\]/g;
        let counter = 1;

        const currentModuleIndex = modules.findIndex(module => module.id === currentModule.id);
        const currentAnswer = modules[currentModuleIndex].answers.find(answer => answer.questionNum === question.questionNum)?.answer;

        html = html.replace(notBoxRegex, (match) => {
            return `<p class='katex'>${match}</p>`;
        });

        html = html.replace(boxRegex, (_match) => {
            const index = counter++;
            let answer = null;

            if (Array.isArray(currentAnswer)) {
                answer = currentAnswer.find(answer => answer[1] === index);
            }
            if (answer) {
                return `
                    <div class='${selectedItemId === answer[0]
                        ? 'digitalSHSATModule__blankItemBackground digitalSHSATModule__blankItemBackground--selected'
                        : 'digitalSHSATModule__blankItemBackground'
                    }'>
                        <div
                            class='digitalSHSATModule__blankItem'
                            data-name='${answer[0]}'
                        >
                            <p>${question.dragAndDropItems.find(item => item.id === answer[0])?.answer}</p>
                        </div>
                        <p>${question.dragAndDropItems.find(item => item.id === answer[0])?.answer}</p>
                    </div>
                `
            }
            return `<div class='digitalSHSATModule__blankQuestionHelperBox' data-index='${index}'></div>`
        });

        setQuestionHelper(html);
    }

    const parseQuestionHelper = (helper: string) => {
        const regex = /\[\[(.*?)\]\]/g;
        let lastIndex = 0;
        let counter = 1;
        let elements = [];
        let match;
      
        while ((match = regex.exec(helper)) !== null) {
            if (match.index > lastIndex) {
                elements.push(<span className='katex'>{helper.slice(lastIndex, match.index)}</span>);
            }

            const index = counter++;
            let answer = null;

            if (Array.isArray(currentAnswer)) {
                answer = currentAnswer.find(answer => answer[1] === index);
            }
            if (answer) {
                elements.push(
                    <span className={selectedItemId === answer[0]
                        ? 'digitalSHSATModule__blankItemBackground digitalSHSATModule__blankItemBackground--selected'
                        : 'digitalSHSATModule__blankItemBackground'
                    }>
                        <span
                            className='digitalSHSATModule__blankItem'
                            data-name={answer[0]}
                            onClick={(event) => { event.stopPropagation(); setSelectedItemId(answer[0]); }}
                        >
                            <span className='katex'>{question.dragAndDropItems.find(item => item.id === answer[0])?.answer}</span>
                        </span>
                        <span className='katex'>{question.dragAndDropItems.find(item => item.id === answer[0])?.answer}</span>
                    </span>
                );
            }
            else {
                elements.push(
                    <span 
                        className='digitalSHSATModule__blankQuestionHelperBox'
                        data-index={index}
                        onClick={() => handleBlankFill(index)}
                    ></span>
                )
            }
          
            lastIndex = regex.lastIndex;
        }
      
        if (lastIndex < helper.length) {
            elements.push(<span className='katex'>{helper.slice(lastIndex)}</span>);
        }
      
        return elements;
    };

    const handleBlankFill = (blankIndex: number, itemId?: number) => {
        if (itemId || selectedItemId) {
            const updatedModules = modules.map(module => {
                if (module.id !== currentModule.id) return module;

                const updatedAnswers = module.answers.map(item => {
                    if (item.questionNum === question.questionNum) {
                        let answer = item.answer;
                        if (Array.isArray(answer)) {
                            answer = answer.filter(entry => (entry[0] !== (itemId ? itemId : selectedItemId) && entry[1] !== blankIndex));
                            answer.push([(itemId ? itemId : selectedItemId), blankIndex]);
                        }

                        return {
                            ...item,
                            answer: answer
                        };
                    }

                    return item;
                });

                return {
                    ...module,
                    answers: updatedAnswers
                };
            });

            autoSave(updatedModules);
            setModules(updatedModules);
            setSelectedItemId(null);
        }
    }

    const handleBlankEmpty = (itemId?: number) => {
        if (itemId || selectedItemId) {
            const updatedModules = modules.map(module => {
                if (module.id !== currentModule.id) return module;

                const updatedAnswers = module.answers.map(item => {
                    if (item.questionNum === question.questionNum) {
                        let answer = item.answer;
                        if (Array.isArray(answer)) {
                            answer = answer.filter(entry => entry[0] !== (itemId ? itemId : selectedItemId));
                        }

                        return {
                            ...item,
                            answer: answer
                        };
                    }

                    return item;
                });


                return {
                    ...module,
                    answers: updatedAnswers
                };
            });

            autoSave(updatedModules);
            setModules(updatedModules);
            setSelectedItemId(null);
        }
    }

    const dragMove = (element: HTMLElement) => {
        let moveX = 0, moveY = 0, x = 0, y = 0, top = 0, left = 0;

        const background = element.parentElement;
    
        const dragMouseDown = (event) => {
            event.preventDefault();

            const itemId = Number(element.dataset.name);
            const rect = element.getBoundingClientRect();
            top = rect.top;
            left = rect.left;
            background.style.visibility = 'visible';
            setSelectedItemId(itemId);

            if (event.type === 'mousedown') {
                x = event.clientX;
                y = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = () => closeDragElement(itemId);
            } else if (event.type === 'touchstart') {
                x = event.touches[0].clientX;
                y = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = () => closeDragElement(itemId);
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                moveX = x - event.clientX;
                moveY = y - event.clientY;
                x = event.clientX;
                y = event.clientY;

            } else if (event.type === 'touchmove') {
                moveX = x - event.touches[0].clientX;
                moveY = y - event.touches[0].clientY;
                x = event.touches[0].clientX;
                y = event.touches[0].clientY;
            }

            if (x > 0 && y > 0 && x < window.innerWidth && y < window.innerHeight) {
                top = top - moveY;
                left = left - moveX;

                element.style.position = 'fixed';
                element.style.top = top + 'px';
                element.style.left = left + 'px';
                element.style.width = element.parentElement.clientWidth + 'px';
                element.style.height = element.parentElement.clientHeight + 'px';
                element.style.zIndex = '2';
            }

            const items = document.getElementById('digitalSHSATModule__blankItems');
            if (items.getBoundingClientRect().top < y && items.getBoundingClientRect().bottom > y
                && items.getBoundingClientRect().left < x && items.getBoundingClientRect().right > x
            ) {
                items.classList.add('digitalSHSATModule__blankItemsDestinationHover');
            }
            else {
                items.classList.remove('digitalSHSATModule__blankItemsDestinationHover');
            }

            const blanks = document.getElementsByClassName('digitalSHSATModule__blankQuestionHelperBox');
            for (let i = 0; i < blanks.length; i++) {
                if (blanks[i].getBoundingClientRect().top < y && blanks[i].getBoundingClientRect().bottom > y
                    && blanks[i].getBoundingClientRect().left < x && blanks[i].getBoundingClientRect().right > x
                ) {
                    blanks[i].classList.add('digitalSHSATModule__blankQuestionHelperBoxHover');
                }
                else {
                    blanks[i].classList.remove('digitalSHSATModule__blankQuestionHelperBoxHover');
                }
            }
        };
    
        const closeDragElement = (itemId?: number) => {
            const items = document.getElementById('digitalSHSATModule__blankItems');
            items.classList.remove('digitalSHSATModule__blankItemsDestinationHover');
            if (items.getBoundingClientRect().top < y && items.getBoundingClientRect().bottom > y
                && items.getBoundingClientRect().left < x && items.getBoundingClientRect().right > x
            ) {
                handleBlankEmpty(Number(itemId));
            }

            const blanks = document.querySelectorAll<HTMLElement>('.digitalSHSATModule__blankQuestionHelperBox');
            for (let i = 0; i < blanks.length; i++) {
                blanks[i].classList.remove('digitalSHSATModule__blankQuestionHelperBoxHover');
                if (blanks[i].getBoundingClientRect().top < y && blanks[i].getBoundingClientRect().bottom > y
                    && blanks[i].getBoundingClientRect().left < x && blanks[i].getBoundingClientRect().right > x
                ) {
                    handleBlankFill(Number(blanks[i].dataset.index), itemId);
                }
            }

            element.style.position = '';
            element.style.top = '';
            element.style.left = '';
            element.style.width = '';
            element.style.height = '';
            element.style.zIndex = '';
            background.style.visibility = '';
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        element.onmousedown = (event) => {
            dragMouseDown(event);
        };

        element.ontouchstart = (event) => {
            dragMouseDown(event);
        };
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        applyKatexWrap(document, {
            enabled: currentModule?.subject === 'English Language Arts',
        });

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useEffect(() => {
        if (question.questionHelper) {
            const helper = parseQuestionHelper(question.questionHelper.questionHelper);
            setQuestionHelper(helper);
        }
    }, [question, modules, selectedItemId]);

    useEffect(() => {
        renderKatex();

        const blankItems = document.getElementsByClassName('digitalSHSATModule__blankItem');

        for (let i = 0; i < blankItems.length; i++) {
            dragMove(blankItems[i] as HTMLElement);
        }
    }, [questionHelper]);

    return (
        <div ref={useOutsideClickHandler(() => setSelectedItemId(null))}>
            <div
                id='digitalSHSATModule__blankItems'
                className={selectedItemId !== null && Array.isArray(currentAnswer) && currentAnswer.find(answer => answer[0] === selectedItemId)
                    ? 'digitalSHSATModule__blankItems digitalSHSATModule__blankItemsDestination'
                    : 'digitalSHSATModule__blankItems'
                }
                onClick={selectedItemId !== null ? () => handleBlankEmpty() : null}
            >
                {question.dragAndDropItems.filter(item => (
                    Array.isArray(currentAnswer) && !currentAnswer.find(answer => answer[0] === item.id)
                )).map(item => (
                    <div
                        key={item.id}
                        className={selectedItemId === item.id
                            ? 'digitalSHSATModule__blankItemBackground digitalSHSATModule__blankItemBackground--selected'
                            : 'digitalSHSATModule__blankItemBackground'
                        }
                    >
                        <div
                            key={item.id}
                            data-name={item.id}
                            className='digitalSHSATModule__blankItem'
                            onClick={(event) => { event.stopPropagation(); setSelectedItemId(item.id); }}
                        >
                            <p className='katex'>{item.answer}</p>
                        </div>
                        <p className='katex'>{item.answer}</p>
                    </div>
                ))}
            </div>
            <div className={selectedItemId
                ? 'digitalSHSATModule__blankQuestionHelper digitalSHSATModule__blankQuestionHelperDestination'
                : 'digitalSHSATModule__blankQuestionHelper'
            }>
                <p className='digitalSHSATModule__blankQuestionHelperText'>
                    {questionHelper}
                </p>
                {/* <div
                    className='digitalSHSATModule__blankQuestionHelperText'
                    dangerouslySetInnerHTML={{ __html: questionHelper }}
                    onClick={(event) => {
                        const target = event.target as HTMLElement;
                        console.log('onClick');
                   
                        if (target.classList.contains('digitalSHSATModule__blankQuestionHelperBox')) {
                            const index = Number(target.dataset.index);

                            handleBlankFill(index);
                        }
                        else if (target.classList.contains('digitalSHSATModule__blankItem')) {
                            const itemId = Number(target.dataset.name);

                            setSelectedItemId(itemId);
                        }
                    }}
                ></div> */}
            </div>
        </div>
    );
}

export default DragAndDropBlank;